// web/src/registry/featureCopy/_part-1.ts —— FEATURE_COPY 的第 1/2 段（65 个元素）
// 拆自 web/src/registry/featureCopy.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { FeatureCopy } from '../featureCopy';

export const FEATURE_COPY_PART_1: Record<string, FeatureCopy> = {
  // ---------- 顶层锚点 ----------
  dashboard: {
    desc: '登录后的第一屏：按角色给出今天的经营与作业概览',
    keywords: ['首页', '概览', '总览', '今日数据', '仪表盘'],
  },
  workbench: {
    desc: '坐席的作业主界面：接听/外呼、实时转写、智能辅助与客户信息同屏',
    keywords: ['工作台', '坐席台', '作业界面', '坐席工作台'],
  },
  inbox: {
    // 知识侧的待办（待审知识/纠错/复核超期/质量与缺口）已随自建问答库整体下线，本页只管流程引擎派给"我"的任务。
    desc: '所有「等我处理」的流程任务集中在这里：工单审批环节、超时升级与转派',
    keywords: ['待办', '待处理', '待我处理', '任务中心'],
  },

  // ---------- 工作台内面板（不是独立页面，但用户会直接找） ----------
  'chat.workspace': {
    desc: '工作台里的在线客服面：同时接多路访客会话、发消息与附件',
    keywords: ['在线会话', '在线聊天', '接会话', '访客消息', '文字客服'],
  },
  'inbound.softphone': {
    desc: '工作台里的「电话机」：拨号、接听、静音、保持、转接、挂断',
    keywords: ['软电话', '话机', '拨号盘', '接听电话', '挂断'],
  },
  'assist.workspace': {
    desc: '工作台里的 AI 旁白：客户在说什么、该接哪句、有什么风险',
    keywords: ['实时辅助', 'AI 建议', '旁白', '实时转写'],
  },
  'knowledge.workspace': {
    // WeKnora 是唯一知识来源：这张卡不再自己收 SSE 画答案区，答案、引用与复制都在子页里渲染。
    desc: '工作台右栏「智能辅助」卡：把客户原话送进 WeKnora 对话页，答案与引用在子页里渲染',
    keywords: ['知识建议', '知识卡片', '智能辅助', '知识召回', '辅助查询'],
  },
  'workflow.inbox': {
    desc: '待办中心页（与顶层锚点同址）：待办/通知/公告三页签——流程任务是作业面，通知与公告是个人消息面',
    keywords: ['我的待办', '待办中心', '待处理清单', '消息中心', '我的消息'],
  },
  // 该页三页签的语料在 _part-2.ts（`workflow.inbox#todo|#notice|#announce`）

  // ---------- 外呼 ----------
  'outbound.jobs': {
    desc: '建外呼任务、导号码、跑机器人外呼并盯进度',
    keywords: ['外呼', '批量外呼', '自动打电话', '电话营销', '号码导入', '外呼作业'],
    actions: ['新建外呼任务', '导入号码', '启动/暂停任务', '导出外呼结果'],
  },
  'outbound.scenes': {
    desc: '定义机器人说什么、怎么转人工、抽哪些结果字段',
    keywords: ['外呼话术', '场景配置', '转人工策略', '结果字段', 'AI 策略'],
  },
  // ---------- 在线客服 ----------
  'chat.sessions': {
    desc: '在线/微信访客会话的回放台：谁接待、聊了什么、走了哪条路径',
    keywords: ['聊天记录', '会话回放', '微信会话', '访客聊天', '在线记录', '对话记录'],
    actions: ['查看会话详情', '导出会话'],
  },
  'chat.quickreplies.personal': {
    desc: '沟通中一键插入的标准话术（公共 + 个人）',
    keywords: ['快捷回复', '常用语', '短语', '话术快捷键', '一键回复'],
  },
  // ---------- 呼入 ----------
  'inbound.calls': {
    desc: '全量话单：每通电话的记录、录音、转写与复盘台账',
    keywords: ['话单', 'CDR', '通话记录', '来电记录', '录音', '通话详情', '通话清单'],
    actions: ['导出话单', '听录音', '下载录音', '查看转写'],
  },
  'inbound.transcripts': {
    desc: '只记得「谁说过什么话」时，按原话跨全部通话反查',
    keywords: ['通话文本', '语音转写', '转写检索', '原话搜索', 'ASR 文本', '文本检索'],
  },
  'inbound.blacklist': {
    desc: '骚扰/拒接号码名录：命中号码的呼入在 FS 侧直接拒接，并留拦截记录',
    keywords: ['黑名单', '拉黑', '拦截', '骚扰拦截', '骚扰号码', '屏蔽号码', '拒接'],
    actions: ['添加号码', '批量导入号码', '解除拦截'],
  },
  // ---------- 机器人 ----------
  'bot.robots': {
    desc: '定义语音通道的 AI 客服是谁：用哪个 ASR/TTS/LLM、用什么音色说话',
    keywords: ['机器人', '语音机器人', '机器人配置', '音色', 'AI 客服', 'TTS', 'AI机器人配置'],
    actions: ['新建机器人', '试听音色'],
  },
  'bot.chat-robots': {
    desc: '文字渠道接待机器人：欢迎语、绑哪个大模型与意图动作',
    keywords: ['在线客服机器人', '聊天机器人', '文字机器人', '自动接待', '机器人问答', '欢迎语', '在线机器人'],
    actions: ['新建在线机器人', '试聊'],
  },
  'bot.intents': {
    desc: '「客户说什么 → 系统做什么」的受控词表与意图规则',
    keywords: ['意图', '意图识别', '意图词表', '语义理解', '意图配置'],
  },
  'bot.voice.defaults': {
    desc: '机器人语音链路的全局默认：AI 接听并发与排队、静音垫、静默提示与 ASR 修正（机器人级可覆盖）',
    keywords: ['语音行为默认', '语音默认', '语音默认行为', '静音垫', 'ASR 修正', 'AI 并发', '呼入并发', '接听并发', '排队'],
  },
  'bot.voice.defaults#backchannel': {
    desc: '机器人播报出声前的预滚静音与句尾留白：回答的前导/尾随静音，以及短提示音（静默提示、再见语、打断询问语）的前导/尾随静音',
    keywords: ['静音垫', '前导静音', '尾随静音', '首字听不清', '短句急促'],
  },
  'bot.voice.defaults#silence': {
    desc: '机器人说完后客户长时间不说话：先播提示语，仍静默则播再见语并挂机',
    keywords: ['静默检测', '静默超时', '客户不说话', '久不说话'],
  },
  'bot.voice.defaults#barge': {
    desc: '客户打断播报后的应答话术，以及机器人出答案慢时的阶梯垫话',
    keywords: ['打断应答', '打断', '垫话', '安抚语'],
  },
  'bot.voice.defaults#transfer': {
    desc: '流程节点要转人工前，先说明原因并征询客户；客户拒绝则继续由 AI 服务',
    keywords: ['转人工确认', '转人工征询', '要不要转人工'],
  },
  'bot.voice.defaults#asr': {
    desc: '短句识别易错：结合上下文用大模型纠错后再进入意图识别/对话链路',
    keywords: ['ASR 修正', '识别纠错', '同音字纠错'],
  },
  'bot.voice.defaults#slow': {
    desc: '识别到老年/听力不便客户时放慢播报语速（触发源、恢复条件与升级口径）',
    keywords: ['适老', '慢速播报', '语速', '放慢'],
  },
  // ---------- 坐席辅助 ----------
  'assist.scripts': {
    desc: '坐席通话/会话中被自动推到眼前的标准话术（也可手动检索）',
    keywords: ['话术', '话术库', '标准话术', '应答话术', '推荐话术', '坐席话术'],
  },
  'assist.products': {
    desc: '套餐推荐的唯一产品源：上下架、资费、卖点',
    keywords: ['产品库', '套餐', '营销产品', '资费', '卖点', '产品推荐'],
  },
  'assist.settings': {
    desc: '实时辅助的总闸与质量档位：要不要起辅助、答案允许从哪来',
    keywords: ['坐席辅助', '辅助设置', '实时辅助', '辅助开关', '检索范围'],
  },
  // ---------- 质检 ----------
  'quality.sampling': {
    desc: '质检详情：质检报告、违规点复核裁定与样本回流',
    keywords: ['质检', '质检详情', '复核', '质检作业', '听录音打分', '质检报告'],
    actions: ['复核裁定', '申诉', '样本回流'],
  },
  'quality.rules': {
    desc: '质检的「标准答案」配置：哪些话术/静默/抢话会被判违规',
    keywords: ['质检规则', '质检项', '违规规则', '规则集', '规则与规则集', '评分项', '质检标准'],
  },
  'quality.templates': {
    desc: '质检模板：从规则集里挑一个或多个并给每个设权重（合计 100），决定「怎么打分」；每个地域的实时质检用语音与在线会话各一条模板，在线会话模板可从语音模板复制后微调',
    keywords: ['质检模板', '模板权重', '评分模板', '规则集权重', '实时模板', '语音模板', '在线会话模板', '权重合计'],
  },
  'quality.jobs': {
    desc: '批量质检任务：圈定数据范围并勾选规则，全量或抽样、立即或夜间调度，任务进度与明细可下钻；任务明细抽屉可就地查看该批次的综合报告与逐通报告',
    keywords: ['批量质检', '质检任务', '任务列表', '夜间质检', '抽样质检', '规则选择', '批次报告', '逐通报告', '批次综合报告'],
    actions: ['新建任务', '运行任务', '复制任务', '取消任务'],
  },
  'quality.jobs#jobs': {
    desc: '批量质检任务列表：圈定范围、运行/暂停/重跑任务、复制任务（同范围再跑一遍）与查看进度明细',
    keywords: ['批量质检任务', '质检任务列表', '复制任务', '重跑任务', '任务明细'],
  },
  'quality.analytics': {
    desc: '回答「质检体系靠不靠谱」：报告汇总（均分/AI 与人工段/趋势/分布）与检出精确率等复核闭环指标同页',
    keywords: ['质检分析', '质检统计', '质检报表', '综合报告'],
  },
  'quality.realtime': {
    desc: '通话/会话进行中的质检告警流水台：实时命中违规并推送告警',
    keywords: ['实时质检', '实时告警', '质检告警', '实时监控规则'],
  },
  // ---------- 知识库 ----------
  // WeKnora 为唯一知识来源：本域只剩三个侧栏条目（知识问答 / 知识管理 / 问答历史），
  // 自建的问答库与全文库此前已整体下线：页面、权限码与检索语料一并摘除。
  'knowledge.chat': {
    desc: '按原文整篇问答：向 WeKnora 提问，得到带引用来源的答案（本系统唯一的知识问答入口）',
    keywords: ['知识问答', '智能问答', '提问', '新对话', '文档问答', '原文问答', '知识对话', 'AI 问答', '问答助手'],
    actions: ['新建对话', '发送问题'],
  },
  'knowledge.chat.history': {
    desc: '知识问答的历史会话：按人归档，只有自己的对话在这里（不是全员共享一份）',
    keywords: ['问答历史', '历史对话', '会话列表', '对话记录', '历史会话', '问答记录'],
    actions: ['打开历史会话'],
  },
  'knowledge.feedback': {
    desc: '点踩处理台：坐席在知识问答里点「无用」并说明后，处理单汇总到这里，班长处理后把结论回写给坐席',
    keywords: ['反馈处理', '点踩处理', '知识反馈', '无用反馈', '反馈审核', '处理单', '反馈闭环'],
    actions: ['处理反馈', '查看处理结论'],
  },
  'knowledge.kbmanage': {
    desc: 'WeKnora 知识管理：左侧知识库与目录树、右侧文档列表（上传、标签、解析状态），改的是喂给「知识问答」的原文库',
    keywords: ['知识管理', '知识库管理', '文档库', '原文库', '知识库目录', '上传文档', '文档标签', '解析状态', 'WeKnora'],
    actions: ['新建知识库', '上传文档', '添加标签', '查看解析状态'],
  },
  // ---------- 客户与工单 ----------
  'ticketing.customers': {
    desc: '客户主数据档案：谁打过电话、聊过天、开过什么工单',
    keywords: ['客户', '客户档案', '客户管理', '客户信息', '客户资料'],
    actions: ['新建客户', '导出客户'],
  },
  'ticketing.tickets': {
    desc: '客户诉求的受理台账：建单、流转、催办、转派',
    keywords: ['工单', '工单管理', '派单', '建单', '工单流转', '诉求'],
    actions: ['新建工单', '转派', '导出工单'],
  },
  'ticketing.settings': {
    desc: '工单配置入口：自定义字段、派单规则、建单模板、审核流程、自动建单',
    keywords: ['工单设置', '客户与工单设置', '工单配置', '派单设置', '工单参数'],
  },
  // ---------- 智能演练 ----------
  'coach.practice': {
    desc: '让坐席跟 AI 扮演的客户对练，结束后按考核要点给分',
    // 关键词保留「陪练」等旧称与口语别名：它是检索别名表，不是界面文案（改名后仍要搜得到）
    keywords: ['陪练', '演练', '对练', '模拟客户', 'AI 演练', '考核', '培训'],
  },
  'coach.history': {
    desc: '演练历史与评分明细',
    keywords: ['演练记录', '陪练记录', '评分记录', '演练成绩'],
  },
  'coach.bank': {
    desc: '演练题目与考核要点',
    keywords: ['题库', '陪练题目', '考核点', '题目管理'],
  },
  // ---------- 运营监控 ----------
  'operations.agents': {
    desc: '按技能组分组看坐席实时状态，点开分组看组内工位明细',
    keywords: ['坐席状态', '在线状态', '坐席监控', '工位', '坐席实况', '技能组分组', '分组统计', '坐席分组'],
  },
  'operations.queues': {
    desc: '排队与话务实况：有多少人在等、谁在接、等了多久；页尾维护溢出与兜底长期配置',
    keywords: ['排队', '队列', '排队情况', '谁在打电话', '话务实况', '等待数', '队列实况', '溢出兜底', '兜底组'],
  },
  'operations.cockpit': {
    desc: '主管值班台：指标异常自动标红并给出根因清单',
    keywords: ['驾驶舱', '值班台', '指标看板', '异常预警', '根因', '运营驾驶舱'],
  },
  'operations.screen': {
    desc: '投屏用的暗色实时大屏，可免登展示',
    keywords: ['大屏', '监控大屏', '投屏', '电视墙', '实时大屏'],
  },
  'operations.team': {
    desc: '班组作战台：本组当班坐席墙（含监听/强插/强拆）、今日状态时长、处置日志与我的待办（只读）',
    keywords: ['班组作战台', '作战台', '本组', '班组看板', '我的人', '坐席墙', '班组实况', '现场管理'],
  },
  'operations.duty': {
    desc: '当班指挥台：全场在线率、排队、20 秒达标率、转人工率与技能组状态（只读，两个页签）',
    keywords: ['当班指挥台', '指挥台', '值班长', '全场', '技能组状态', '当班', '交接', '值班看板', '队列状态'],
  },
  'operations.duty.control': {
    desc: '人员分配：把 B 组的人临时调到排队的 A 组；从技能组状态一键带目标组跳过来',
    keywords: ['人员分配', '当班处置', '分配人员', '跨组支援', '自动挑人', '调人'],
  },
  'operations.incidents': {
    desc: '重大事件台账：新建、处理、关闭，关闭时把总结写进备注',
    keywords: ['重大事件', '事件台账', '新建', '处理', '关闭', '总结备注', '事故', '运营处置'],
  },
  'operations.shifts': {
    desc: '坐席排班编制与对账，班次即接线策略',
    keywords: ['排班', '班次', '值班表', '人力排班', '排班表'],
  },
  'operations.settings': {
    desc: '运营监控侧设置入口：驾驶舱预警阈值、坐席运营参数',
    keywords: ['运营设置', '运营监控设置', '监控设置', '预警设置', '坐席运营设置'],
  },
  // ---------- 客户洞察 ----------
  'customer-insights.satisfaction': {
    desc: '电话与在线两条渠道的客户评分台账，带好评/差评口径统计',
    keywords: ['满意度', '评分', '好评差评', '评价记录', '满意度评价'],
  },
  'customer-insights.voc': {
    desc: '把客户原话打标归类，形成可整改的 VOC 议题',
    keywords: ['VOC', '客户之声', '打标', '舆情', '客户反馈', '议题'],
  },
  'customer-insights.settings': {
    desc: '满意度与 VOC 的设置入口：开关、分数标签、话术',
    keywords: ['满意度设置', '客户洞察设置', '评价设置', 'VOC 设置', '洞察设置'],
  },
  // ---------- 报表中心 ----------
  'analytics.report.core': {
    desc: '核心运营报表：区间内的量、质、势与分时段趋势',
    keywords: ['报表', '核心报表', '运营报表', '指标', 'Top10'],
    actions: ['导出报表'],
  },
  'analytics.report.ai': {
    desc: 'AI 分流漏斗、LLM 账单与「省了多少人力」的产能等价',
    keywords: ['AI 分析', 'AI 报表', 'LLM 账单', '成本分析', '分流漏斗'],
  },
  'analytics.report.service': {
    desc: '服务水平报表：20 秒应答率、呼损率与平均等待',
    keywords: ['服务报表', '服务水平', '应答率', '呼损', '效率报表'],
  },
  // ---------- AI 能力 ----------
  'ai.providers': {
    desc: 'AI 能力总开关与总账：六类能力各自绑哪个平台、密钥与连通性测试',
    keywords: ['AI 供应商', '模型供应商', '密钥', 'ASR', 'TTS', 'LLM', '大模型配置'],
    actions: ['新增供应商', '测试连通性'],
  },};


